import "./print.page.css";
import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import dayjs from "dayjs";
import { getCustomerPortalRosterQuery, getCustomerPortalServiceSummaryQuery } from "@src/util/query/portal.query";
import { formatHours, groupRows, officerName, sourceLabel, stateMeta, timeRange } from "@src/util/portal/dutyState";
import { SCORECARD, rateTone } from "@src/page/serviceReportPage/serviceReport.page";

// Printable reports (opened in a new tab from Roster / Reports; the browser's
// print dialog saves them as PDF): the service report, and the roster / hours
// report. ?from=YYYY-MM-DD&to=YYYY-MM-DD[&site=][&kind=hours]

const readCustomer = () => {
    try { return JSON.parse(localStorage.getItem("customerDetail")) || {}; } catch (e) { return {}; }
};

const usePrintRange = () => {
    const [params] = useSearchParams();
    const from = dayjs(params.get("from") || undefined).startOf("day");
    const to = dayjs(params.get("to") || undefined).endOf("day");
    const siteId = params.get("site") || null;
    const variables = useMemo(() => ({ from: from.toISOString(), to: to.toISOString(), siteId }),
        [from.valueOf(), to.valueOf(), siteId]); // eslint-disable-line react-hooks/exhaustive-deps
    return { from, to, siteId, variables, kind: params.get("kind"), by: params.get("by") };
};

// opens the print dialog once, when the data is in
const useAutoPrint = (ready) => {
    const printed = useRef(false);
    useEffect(() => {
        if (!ready || printed.current) return;
        printed.current = true;
        const t = setTimeout(() => window.print(), 500);
        return () => clearTimeout(t);
    }, [ready]);
};

const Sheet = ({ title, from, to, siteName, loading, children }) => {
    const customer = readCustomer();
    const provider = customer?.tenantDetail?.legalname;
    return (
        <div className="pr-page">
            <div className="pr-toolbar">
                <span>{loading ? "Preparing your report…" : "Use Save as PDF in the print dialog to keep a copy."}</span>
                <div>
                    <button type="button" onClick={() => window.print()} disabled={loading}>Print / Save as PDF</button>
                    <button type="button" className="pr-secondary" onClick={() => window.close()}>Close</button>
                </div>
            </div>
            <div className="pr-sheet">
                <header className="pr-head">
                    <div>
                        <div className="pr-eyebrow">{provider ? `${provider} · ` : ""}Customer portal</div>
                        <h1>{title}</h1>
                        <div className="pr-meta">
                            <b>{customer?.customername}</b>{siteName ? ` · ${siteName}` : " · All sites"} · {from.format("D MMM YYYY")} – {to.format("D MMM YYYY")}
                        </div>
                    </div>
                    {customer?.metadata?.logolink && <img className="pr-logo" src={customer.metadata.logolink} alt="" />}
                </header>
                {loading ? <div className="pr-loading">Loading…</div> : children}
                <footer className="pr-foot">Generated {dayjs().format("D MMM YYYY, HH:mm")}</footer>
            </div>
        </div>
    );
};

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${Math.round(v)}%`);

export const ServicePrint = () => {
    const { from, to, siteId, variables } = usePrintRange();
    const { data, loading } = useQuery(getCustomerPortalServiceSummaryQuery, { variables, fetchPolicy: "network-only" });
    const summary = data?.getCustomerPortalServiceSummary?.response;
    const t = summary?.totals;
    const siteName = siteId ? summary?.bySite?.find((s) => s.siteId === siteId)?.siteName : null;
    useAutoPrint(!!summary);

    return (
        <Sheet title="Security service report" from={from} to={to} siteName={siteName} loading={loading || !summary}>
            {t && (
                <>
                    <div className="pr-summary">
                        <b>{t.shifts}</b> duties ({t.guardingShifts} guarding, {t.mobileJobs} mobile) · <b>{formatHours(t.scheduledHours)}</b> booked · <b>{t.missed}</b> missed · <b>{t.incidents}</b> incident{t.incidents === 1 ? "" : "s"}
                    </div>
                    <div className="pr-score">
                        {SCORECARD.map((s) => (
                            <div key={s.key} className={`pr-tile pr-tone-${rateTone(t[s.key])}`}>
                                <span className="pr-tile-label">{s.label}</span>
                                <span className="pr-tile-value">{fmtPct(t[s.key])}</span>
                                <span className="pr-tile-sub">{s.sub(t)}</span>
                            </div>
                        ))}
                    </div>
                    <p className="pr-note">On target 95% or more · 85–94% close · below 85% needs attention. Only duties that have started count towards attendance and punctuality.</p>

                    <h2>By site</h2>
                    <table className="pr-table">
                        <thead>
                            <tr><th>Site</th><th>Duties</th><th>Cover</th><th>Attendance</th><th>On time</th><th>Hours delivered</th><th>Welfare</th><th>Patrols</th><th>Missed</th><th>Incidents</th></tr>
                        </thead>
                        <tbody>
                            {summary.bySite.map((s) => (
                                <tr key={s.siteId}>
                                    <td>{s.siteName}</td><td>{s.shifts}</td><td>{fmtPct(s.fillRate)}</td><td>{fmtPct(s.attendanceRate)}</td><td>{fmtPct(s.punctuality)}</td>
                                    <td>{formatHours(s.deliveredHours)} / {formatHours(s.pastScheduledHours)}</td><td>{fmtPct(s.welfareRate)}</td><td>{fmtPct(s.patrolRate)}</td><td>{s.missed}</td><td>{s.incidents}</td>
                                </tr>
                            ))}
                            {summary.bySite.length === 0 && <tr><td colSpan={10}>No duties in this period.</td></tr>}
                        </tbody>
                    </table>

                    <h2>Missed or uncovered duties</h2>
                    {summary.attention.length === 0 ? <p>Every duty in this period was covered.</p> : (
                        <table className="pr-table">
                            <thead><tr><th>Date</th><th>Time</th><th>Site</th><th>Duty</th><th>Status</th></tr></thead>
                            <tbody>
                                {summary.attention.map((r) => (
                                    <tr key={r._id}><td>{dayjs(r.start).format("ddd D MMM")}</td><td>{timeRange(r.start, r.end)}</td><td>{r.siteName}</td><td>{r.dutyType || sourceLabel(r)}</td><td>{stateMeta(r.state).label}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    <h2>Incidents</h2>
                    {summary.incidents.length === 0 ? <p>No incidents reported in this period.</p> : (
                        <table className="pr-table">
                            <thead><tr><th>Date</th><th>Site</th><th>Type</th></tr></thead>
                            <tbody>
                                {summary.incidents.map((i) => (
                                    <tr key={i._id}><td>{dayjs(i.createdAt).format("D MMM YYYY, HH:mm")}</td><td>{i.siteName}</td><td>{i.incidentType}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}
        </Sheet>
    );
};

// kind=hours: the hours report (booked vs on-duty hours, totals by site);
// otherwise the roster as a printable list
export const RosterPrint = () => {
    const { from, to, siteId, variables, kind, by } = usePrintRange();
    const isHours = kind === "hours";
    const cols = isHours ? 10 : 7;
    const { data, loading } = useQuery(getCustomerPortalRosterQuery, { variables, fetchPolicy: "network-only" });
    const roster = data?.getCustomerPortalRoster?.response;
    const rows = useMemo(() => roster?.rows || [], [roster]);
    const siteName = siteId ? roster?.sites?.[0]?.sitename : null;
    useAutoPrint(!!roster);

    const bySite = useMemo(() => {
        const map = new Map();
        rows.forEach((r) => {
            const s = map.get(r.siteName) || { siteName: r.siteName, duties: 0, hours: 0, actual: 0 };
            s.duties += 1;
            s.hours += r.hours || 0;
            s.actual += r.actualHours || 0;
            map.set(r.siteName, s);
        });
        return [...map.values()].sort((a, b) => a.siteName.localeCompare(b.siteName));
    }, [rows]);
    const totalActual = rows.reduce((sum, r) => sum + (r.actualHours || 0), 0);
    // ?by=site|officer (the roster's "View by"): a section per site / officer
    const sections = useMemo(() => (by === "site" || by === "officer" ? groupRows(rows, by) : [{ key: "all", rows }]), [rows, by]);

    return (
        <Sheet title={`${isHours ? "Hours report" : "Schedule"}${by === "site" ? " by site" : by === "officer" ? " by officer" : ""}`} from={from} to={to} siteName={siteName} loading={loading || !roster}>
            {roster && (
                <>
                    <div className="pr-summary">
                        <b>{rows.length}</b> duties · <b>{formatHours(roster.totalHours)}</b> booked{isHours ? <> · <b>{formatHours(totalActual)}</b> on duty</> : null}
                    </div>
                    <table className="pr-table">
                        <thead>
                            <tr>
                                <th>Date</th><th>Time</th><th>Site</th><th>Duty</th><th>Officer</th><th>Booked</th>
                                {isHours && <><th>Clock in</th><th>Clock out</th><th>On duty</th></>}
                                <th>Status</th>
                            </tr>
                        </thead>
                        {sections.map((g) => (
                        <tbody key={g.key}>
                            {g.label && (
                                <tr className="pr-group"><td colSpan={cols}>{g.label} · {g.rows.length} dut{g.rows.length === 1 ? "y" : "ies"} · {formatHours(g.hours)}</td></tr>
                            )}
                            {g.rows.map((r) => (
                                <tr key={r._id}>
                                    <td>{dayjs(r.start).format("ddd D MMM")}</td>
                                    <td>{timeRange(r.start, r.end)}</td>
                                    <td>{r.siteName}</td>
                                    <td>{r.dutyType || sourceLabel(r)}</td>
                                    <td>{officerName(r)}</td>
                                    <td>{formatHours(r.hours)}</td>
                                    {isHours && (
                                        <>
                                            <td>{r.clockIn ? dayjs(r.clockIn.at).format("HH:mm") : "—"}</td>
                                            <td>{r.clockOut ? dayjs(r.clockOut.at).format("HH:mm") : "—"}</td>
                                            <td>{formatHours(r.actualHours)}</td>
                                        </>
                                    )}
                                    <td>{stateMeta(r.state).label}</td>
                                </tr>
                            ))}
                            {rows.length === 0 && <tr><td colSpan={cols}>No duties in this period.</td></tr>}
                        </tbody>
                        ))}
                    </table>

                    {isHours && bySite.length > 0 && (
                        <>
                            <h2>Totals by site</h2>
                            <table className="pr-table">
                                <thead><tr><th>Site</th><th>Duties</th><th>Booked</th><th>On duty</th></tr></thead>
                                <tbody>
                                    {bySite.map((s) => (
                                        <tr key={s.siteName}><td>{s.siteName}</td><td>{s.duties}</td><td>{formatHours(s.hours)}</td><td>{formatHours(s.actual)}</td></tr>
                                    ))}
                                    <tr className="pr-total"><td>Total</td><td>{rows.length}</td><td>{formatHours(roster.totalHours)}</td><td>{formatHours(totalActual)}</td></tr>
                                </tbody>
                            </table>
                        </>
                    )}
                </>
            )}
        </Sheet>
    );
};
